/**
 * AnalyticsPage — Faza 5.
 * Range picker → Donut (income/expense toggle) → Line chart (lazy Recharts) →
 * Savings card → Subscriptions card.
 *
 * Drill-down per kategoria — zaplanowane Faza 5.5 (TransactionList z filtrem cat).
 */
import { lazy, Suspense, useMemo, useState } from 'react';
import { RangePicker } from '../components/analytics/RangePicker';
import { Donut } from '../components/analytics/Donut';
import { SavingsCard } from '../components/analytics/SavingsCard';
import { SubscriptionsCard } from '../components/analytics/SubscriptionsCard';
import { CategoryDrillDown } from '../components/analytics/CategoryDrillDown';
import { WalletScroller } from '../components/dashboard/WalletScroller';
import SegmentedControl from '../components/ui/SegmentedControl';
import { useTransactionsStore } from '../features/transactions/transactionsStore';
import { useCategoriesStore } from '../features/categories/categoriesStore';
import { useWalletsStore } from '../features/wallets/walletsStore';
import { useTxModal } from '../features/transactions/txModalStore';
import {
  type RangeKey,
  filterTxByRange,
  categoryBreakdown,
  dailyBalance,
  savingsRate,
  listSubscriptions,
} from '../features/analytics/selectors';

const BalanceLineChart = lazy(() => import('../components/analytics/BalanceLineChart'));
const IncomeExpenseBarChart = lazy(() => import('../components/analytics/IncomeExpenseBarChart'));

export function AnalyticsPage() {
  const transactions = useTransactionsStore((s) => s.transactions);
  const categories = useCategoriesStore((s) => s.categories);
  const wallets = useWalletsStore((s) => s.wallets);
  const selectedWalletId = useWalletsStore((s) => s.selectedWalletId);

  const [range, setRange] = useState<RangeKey>('1m');
  const [donutMode, setDonutMode] = useState<'expense' | 'income'>('expense');
  const [drillCategoryId, setDrillCategoryId] = useState<string | null>(null);

  const selectedType = wallets.find((w) => w.id === selectedWalletId)?.type;
  const walletFilter =
    selectedWalletId === 'all' || !selectedType || selectedType === 'shared'
      ? null
      : selectedWalletId;

  const inRange = useMemo(
    () => filterTxByRange(transactions, walletFilter, range),
    [transactions, walletFilter, range],
  );

  const breakdown = useMemo(
    () => categoryBreakdown(inRange, categories, donutMode),
    [inRange, categories, donutMode],
  );

  const balanceSeries = useMemo(
    () => dailyBalance(transactions, walletFilter, range),
    [transactions, walletFilter, range],
  );

  const totals = useMemo(() => {
    let income = 0;
    let expense = 0;
    for (const t of inRange) {
      if (t.type === 'income') income += t.amount;
      else if (t.type === 'expense') expense += t.amount;
    }
    return { income, expense };
  }, [inRange]);

  const subs = useMemo(() => listSubscriptions(transactions, categories), [transactions, categories]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-white">Analityka</h1>
      </div>

      <WalletScroller />

      <RangePicker value={range} onChange={setRange} />

      {/* Donut card */}
      <div className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-4">
        <SegmentedControl<'expense' | 'income'>
          options={[
            { key: 'expense', label: 'Wydatki' },
            { key: 'income', label: 'Przychody' },
          ]}
          value={donutMode}
          onChange={setDonutMode}
          fullWidth
        />
        <div className="mt-4">
          <Donut
            rows={breakdown.rows}
            total={breakdown.total}
            mode={donutMode}
            onPick={setDrillCategoryId}
          />
        </div>
      </div>

      {/* Line chart */}
      <div className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-4">
        <p className="text-xs uppercase tracking-wide text-slate-400 font-bold mb-2">
          Saldo w czasie
        </p>
        <Suspense fallback={<div className="h-[220px] flex items-center justify-center text-sm text-slate-400">Ładowanie wykresu...</div>}>
          <BalanceLineChart data={balanceSeries} />
        </Suspense>
      </div>

      {/* Bar chart income/expense */}
      <div className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-4">
        <p className="text-xs uppercase tracking-wide text-slate-400 font-bold mb-2">
          Przychody vs wydatki
        </p>
        <Suspense fallback={<div className="h-[220px] flex items-center justify-center text-sm text-slate-400">Ładowanie wykresu...</div>}>
          <IncomeExpenseBarChart data={balanceSeries} />
        </Suspense>
      </div>

      <SavingsCard income={totals.income} expense={totals.expense} rate={savingsRate(totals.income, totals.expense)} />
      <SubscriptionsCard subscriptions={subs} />

      {drillCategoryId !== null && (
        <CategoryDrillDown
          category={categories.find((c) => c.id === drillCategoryId) ?? null}
          transactions={inRange}
          onClose={() => setDrillCategoryId(null)}
          onPickTx={(tx) => {
            setDrillCategoryId(null);
            useTxModal.getState().openEdit(tx);
          }}
        />
      )}
    </div>
  );
}
